import React, { useState } from "react";
import { Check, ChevronDown, ChevronRight, Loader2, Sparkles } from "lucide-react";

interface StepsTraceProps {
  /** Ordered list of step labels (the AI "thinking" statuses). */
  steps: string[];
  /** True while the pipeline is still running (last step shows a spinner). */
  active?: boolean;
}

/**
 * Claude-style collapsible "thinking" trace. While `active`, it stays expanded and
 * shows a spinner on the current step. Once finished it collapses to a one-line
 * summary the user can expand to review every step the assistant went through.
 */
const StepsTrace: React.FC<StepsTraceProps> = ({ steps, active = false }) => {
  // Expanded while the work is happening; collapsed once it's done.
  const [open, setOpen] = useState<boolean>(active);

  // Keep it open while streaming even if the user hasn't interacted.
  React.useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const visibleSteps = (steps || []).reduce<string[]>((acc, step) => {
    const label = String(step || "").trim();
    if (!label) return acc;

    const key = label.toLowerCase().replace(/\s+/g, " ");
    const alreadyShown = acc.some(
      (existing) => existing.toLowerCase().replace(/\s+/g, " ") === key,
    );
    if (!alreadyShown) acc.push(label);
    return acc;
  }, []);

  if (visibleSteps.length === 0) return null;

  if (active) {
    return (
      <div
        style={{
          border: "1px solid #ececf1",
          background: "#fafafa",
          borderRadius: 12,
          marginBottom: 10,
          overflow: "hidden",
          fontSize: 12.5,
        }}
      >
        <div style={{ padding: "8px 12px 10px 12px" }}>
          {visibleSteps.map((step, idx) => {
            const isRunning = idx === visibleSteps.length - 1;
            return (
              <div
                key={`${idx}-${step}`}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "3px 0",
                  position: "relative",
                  paddingLeft: 18,
                }}
              >
                {idx !== visibleSteps.length - 1 && (
                  <span
                    style={{
                      position: "absolute",
                      left: 6,
                      top: 16,
                      bottom: -3,
                      width: 1,
                      background: "#e4e4e7",
                    }}
                  />
                )}
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 3,
                    width: 13,
                    height: 13,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isRunning ? (
                    <Loader2 size={12} className="spin" style={{ color: "#0C447C" }} />
                  ) : (
                    <Check size={12} style={{ color: "#16a34a" }} />
                  )}
                </span>
                <span
                  style={{
                    color: isRunning ? "#0C447C" : "#6b7280",
                    fontWeight: isRunning ? 600 : 400,
                    lineHeight: 1.5,
                  }}
                >
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const headerLabel = `Worked through ${visibleSteps.length} step${visibleSteps.length > 1 ? "s" : ""}`;
  const listSteps = visibleSteps;
  const hasListSteps = listSteps.length > 0;

  return (
    <div
      style={{
        border: "1px solid #ececf1",
        background: "#fafafa",
        borderRadius: 12,
        marginBottom: 10,
        overflow: "hidden",
        fontSize: 12.5,
      }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => {
          if (hasListSteps) setOpen((o) => !o);
        }}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: "transparent",
          border: "none",
          cursor: hasListSteps ? "pointer" : "default",
          textAlign: "left",
          color: "#3f3f46",
        }}
      >
        {active ? (
          <Loader2 size={14} className="spin" style={{ color: "#0C447C" }} />
        ) : (
          <Sparkles size={14} style={{ color: "#0C447C" }} />
        )}
        <span style={{ fontWeight: 600, flex: 1, color: active ? "#0C447C" : "#52525b" }}>
          {headerLabel}
        </span>
        {hasListSteps && (
          open ? (
            <ChevronDown size={15} style={{ color: "#a1a1aa" }} />
          ) : (
            <ChevronRight size={15} style={{ color: "#a1a1aa" }} />
          )
        )}
      </button>

      {/* Steps list */}
      {open && hasListSteps && (
        <div style={{ padding: "2px 12px 10px 12px" }}>
          {listSteps.map((step, idx) => {
            const isRunning = false;
            return (
              <div
                key={`${idx}-${step}`}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "3px 0",
                  position: "relative",
                  paddingLeft: 18,
                }}
              >
                {/* connector rail */}
                {idx !== listSteps.length - 1 && (
                  <span
                    style={{
                      position: "absolute",
                      left: 6,
                      top: 16,
                      bottom: -3,
                      width: 1,
                      background: "#e4e4e7",
                    }}
                  />
                )}
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 3,
                    width: 13,
                    height: 13,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isRunning ? (
                    <Loader2 size={12} className="spin" style={{ color: "#0C447C" }} />
                  ) : (
                    <Check size={12} style={{ color: "#16a34a" }} />
                  )}
                </span>
                <span
                  style={{
                    color: isRunning ? "#0C447C" : "#6b7280",
                    fontWeight: isRunning ? 600 : 400,
                    lineHeight: 1.5,
                  }}
                >
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StepsTrace;
